import React, { useState, useEffect } from 'react';
import { Target, Users, Trophy, Clock, Play, X, ChevronDown, ChevronUp, Lock, UserPlus, UserCheck, UserX, Check, Settings, MapPin, Link2, Copy, Shuffle, Crown, History, ArrowRight, RefreshCw, Trash2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './FFAMultistep.css';

const FFAMultistepCard = ({ tournament, user, onUpdate, t, currentLanguage, socket }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showLobbiesModal, setShowLobbiesModal] = useState(false);
  const [showStartMatchModal, setShowStartMatchModal] = useState(false);
  const [showSelectWinnerModal, setShowSelectWinnerModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [joinPassword, setJoinPassword] = useState('');

  // Match start form
  const [selectedLobby, setSelectedLobby] = useState(null);
  const [roomId, setRoomId] = useState('');
  const [roomUrl, setRoomUrl] = useState('');
  const [isFinals, setIsFinals] = useState(false);

  const { ffaMultistepData } = tournament;

  // Socket listener for updates
  useEffect(() => {
    if (!socket) return;

    const handleFFAUpdate = (data) => {
      if (data.tournamentId !== tournament._id) return;
      console.log('FFA Update received:', data);
      if (onUpdate) onUpdate();
    };

    socket.on('ffaMultistepUpdated', handleFFAUpdate);
    return () => socket.off('ffaMultistepUpdated', handleFFAUpdate);
  }, [socket, tournament._id, onUpdate]);

  // Role checks
  const isAdmin = ['main_admin', 'manager'].includes(user?.role);
  const userId = user?._id?.toString?.() || user?._id;

  // Helper to extract player ID correctly (handles both populated and non-populated userId)
  const getPlayerId = (player) => {
    if (!player?.userId) return null;
    // If userId is populated (has _id property), use that; otherwise use userId directly
    return (player.userId._id || player.userId)?.toString?.() || player.userId.toString();
  };

  // Player status
  const isPending = ffaMultistepData?.pendingPlayers?.some(
    p => p.userId?.toString() === userId || p.userId?._id?.toString() === userId
  );
  const isApproved = ffaMultistepData?.approvedPlayers?.some(
    p => p.userId?.toString() === userId || p.userId?._id?.toString() === userId
  );

  // Counts
  const pendingCount = ffaMultistepData?.pendingPlayers?.length || 0;
  const approvedCount = ffaMultistepData?.approvedPlayers?.length || 0;
  const maxPlayers = ffaMultistepData?.maxPlayers || 64;
  const lobbiesCount = ffaMultistepData?.lobbies?.length || 0;
  const completedLobbies = ffaMultistepData?.lobbies?.filter(l => l.status === 'completed').length || 0;

  const getStatusColor = () => {
    const status = ffaMultistepData?.ffaStatus;
    switch (status) {
      case 'registration': return 'status-registration';
      case 'pending_approval': return 'status-pending';
      case 'round1_lobby':
      case 'round1_in_progress': return 'status-round1';
      case 'finals_lobby':
      case 'finals_in_progress': return 'status-finals';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getStatusLabel = () => {
    const status = ffaMultistepData?.ffaStatus;
    switch (status) {
      case 'registration': return t('registration', 'Registration Open');
      case 'pending_approval': return t('pendingApproval', 'Pending Approval');
      case 'round1_lobby': return t('round1Lobby', 'Round 1 - Lobbies Ready');
      case 'round1_in_progress': return t('round1InProgress', 'Round 1 In Progress');
      case 'finals_lobby': return t('finalsLobby', 'Finals Lobby Ready');
      case 'finals_in_progress': return t('finalsInProgress', 'Finals In Progress');
      case 'completed': return t('completed', 'Completed');
      case 'cancelled': return t('cancelled', 'Cancelled');
      default: return status;
    }
  };

  // Request to join
  const handleRequestJoin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/request-join`, {
        password: joinPassword
      });
      toast.success(response.data.message || t('joinRequested', 'Join request submitted!'));
      setShowJoinModal(false);
      setJoinPassword('');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('joinFailed', 'Failed to submit join request'));
    } finally {
      setLoading(false);
    }
  };

  // Approve player
  const handleApprovePlayer = async (playerId) => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/approve-player`, { playerId });
      toast.success(response.data.message || t('playerApproved', 'Player approved!'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('approveFailed', 'Failed to approve player'));
    } finally {
      setLoading(false);
    }
  };

  // Approve all
  const handleApproveAll = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/approve-all`);
      toast.success(response.data.message || t('allApproved', 'All players approved!'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('approveAllFailed', 'Failed to approve all'));
    } finally {
      setLoading(false);
    }
  };

  // Reject player
  const handleRejectPlayer = async (playerId) => {
    if (!window.confirm(t('confirmReject', 'Reject this player?'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/reject-player`, { playerId });
      toast.success(response.data.message || t('playerRejected', 'Player rejected'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('rejectFailed', 'Failed to reject player'));
    } finally {
      setLoading(false);
    }
  };

  // Remove approved player
  const handleRemovePlayer = async (playerId) => {
    if (!window.confirm(t('confirmRemove', 'Remove this player?'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/remove-player`, { playerId });
      toast.success(response.data.message || t('playerRemoved', 'Player removed'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('removeFailed', 'Failed to remove player'));
    } finally {
      setLoading(false);
    }
  };

  // Create lobbies
  const handleCreateLobbies = async (mode = 'random') => {
    if (!window.confirm(t('confirmCreateLobbies', 'Create lobbies? Players will be assigned ' + (mode === 'random' ? 'randomly' : 'manually') + '.'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/create-lobbies`, { mode });
      toast.success(response.data.message || t('lobbiesCreated', 'Lobbies created!'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('createLobbiesFailed', 'Failed to create lobbies'));
    } finally {
      setLoading(false);
    }
  };

  // Move player between lobbies
  const handleMovePlayer = async (playerId, fromLobby, toLobby) => {
    if (fromLobby === toLobby) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/move-player`, {
        playerId,
        fromLobby,
        toLobby
      });
      toast.success(response.data.message || t('playerMoved', 'Player moved!'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('moveFailed', 'Failed to move player'));
    } finally {
      setLoading(false);
    }
  };

  // Start match
  const handleStartMatch = async () => {
    if (!roomId.trim()) {
      toast.error(t('roomIdRequired', 'Room ID is required'));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/start-match`, {
        lobbyNumber: selectedLobby?.lobbyNumber,
        roomId: roomId.trim(),
        roomUrl: roomUrl.trim(),
        isFinals
      });
      toast.success(response.data.message || t('matchStarted', 'Match started!'));
      setShowStartMatchModal(false);
      setRoomId('');
      setRoomUrl('');
      setSelectedLobby(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('startMatchFailed', 'Failed to start match'));
    } finally {
      setLoading(false);
    }
  };

  // Select winner
  const handleSelectWinner = async (winnerId) => {
    if (!window.confirm(t('confirmSelectWinner', 'Select this player as the winner?'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/select-winner`, {
        lobbyNumber: selectedLobby?.lobbyNumber,
        winnerId,
        isFinals
      });
      toast.success(response.data.message || t('winnerSelected', 'Winner selected!'));
      setShowSelectWinnerModal(false);
      setSelectedLobby(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('selectWinnerFailed', 'Failed to select winner'));
    } finally {
      setLoading(false);
    }
  };

  // Cancel tournament
  const handleCancel = async () => {
    if (!window.confirm(t('confirmCancel', 'Cancel this tournament? Prize pool will be refunded.'))) return;
    setLoading(true);
    try {
      const response = await axios.post(`/api/ffa-multistep/${tournament._id}/cancel`);
      toast.success(response.data.message || t('tournamentCancelled', 'Tournament cancelled'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('cancelFailed', 'Failed to cancel tournament'));
    } finally {
      setLoading(false);
    }
  };

  // Delete tournament permanently
  const handleDelete = async () => {
    if (!window.confirm(t('confirmDelete', 'PERMANENTLY DELETE this tournament? This cannot be undone!'))) return;
    if (!window.confirm(t('confirmDeleteAgain', 'Are you REALLY sure? This will remove the tournament completely.'))) return;
    setLoading(true);
    try {
      const response = await axios.delete(`/api/ffa-multistep/${tournament._id}`);
      toast.success(response.data.message || t('tournamentDeleted', 'Tournament deleted'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || t('deleteFailed', 'Failed to delete tournament'));
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('copied', 'Copied to clipboard!'));
  };

  // Get player's current room info (if in active match)
  const getMyRoomInfo = () => {
    // Check round 1 lobbies
    for (const lobby of (ffaMultistepData?.lobbies || [])) {
      if (lobby.status === 'active' && lobby.roomPublished) {
        const isInLobby = lobby.players.some(
          p => p.userId?.toString() === userId || p.userId?._id?.toString() === userId
        );
        if (isInLobby) {
          return { roomId: lobby.roomId, roomUrl: lobby.roomUrl, lobbyNumber: lobby.lobbyNumber };
        }
      }
    }
    // Check finals
    if (ffaMultistepData?.finalsLobby?.status === 'active' && ffaMultistepData?.finalsLobby?.roomPublished) {
      const isInFinals = ffaMultistepData.finalsLobby.players.some(
        p => p.userId?.toString() === userId || p.userId?._id?.toString() === userId
      );
      if (isInFinals) {
        return { roomId: ffaMultistepData.finalsLobby.roomId, roomUrl: ffaMultistepData.finalsLobby.roomUrl, isFinals: true };
      }
    }
    return null;
  };

  const myRoomInfo = getMyRoomInfo();

  return (
    <div className={`ffa-multistep-card ${getStatusColor()}`}>
      {/* Header */}
      <div className="ffa-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="ffa-card-title">
          <Target className="w-5 h-5 text-orange-400" />
          <span>{t('ffaTournament', 'FFA Tournament')}</span>
          <span className={`ffa-status-badge ${getStatusColor()}`}>{getStatusLabel()}</span>
        </div>
        <div className="ffa-card-info">
          <span className="ffa-players-count">
            <Users className="w-4 h-4" />
            {approvedCount}/{maxPlayers}
          </span>
          <span className="ffa-prize">
            <Trophy className="w-4 h-4" />
            {ffaMultistepData?.prizePool || 0} pts
          </span>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>

      {/* My Room Info (for participants) */}
      {myRoomInfo && (
        <div className="ffa-my-room-info">
          <div className="room-info-header">
            <MapPin className="w-4 h-4" />
            <span>{myRoomInfo.isFinals ? t('yourFinalsRoom', 'Your Finals Room') : t('yourRoom', 'Your Room') + ' (Lobby ' + myRoomInfo.lobbyNumber + ')'}</span>
          </div>
          <div className="room-info-details">
            <div className="room-id">
              <span>{t('roomId', 'Room ID')}:</span>
              <strong>{myRoomInfo.roomId}</strong>
              <button onClick={() => copyToClipboard(myRoomInfo.roomId)} className="copy-btn">
                <Copy className="w-3 h-3" />
              </button>
            </div>
            {myRoomInfo.roomUrl && (
              <div className="room-url">
                <a href={myRoomInfo.roomUrl} target="_blank" rel="noopener noreferrer">
                  <Link2 className="w-3 h-3" /> {t('joinRoom', 'Join Room')}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expanded Content */}
      {expanded && (
        <div className="ffa-card-content">
          {/* Registration Phase */}
          {ffaMultistepData?.ffaStatus === 'registration' && (
            <div className="ffa-registration-section">
              {/* Join Button for non-participants */}
              {!isPending && !isApproved && !isAdmin && (
                <button className="ffa-join-btn" onClick={() => setShowJoinModal(true)}>
                  <UserPlus className="w-4 h-4" />
                  {t('requestToJoin', 'Request to Join')}
                </button>
              )}

              {/* Pending Status */}
              {isPending && (
                <div className="ffa-pending-status">
                  <Clock className="w-4 h-4" />
                  {t('waitingApproval', 'Waiting for admin approval...')}
                </div>
              )}

              {/* Approved Status */}
              {isApproved && !isAdmin && (
                <div className="ffa-approved-status">
                  <UserCheck className="w-4 h-4" />
                  {t('youAreApproved', 'You are approved! Wait for tournament to start.')}
                </div>
              )}

              {/* Admin Controls */}
              {isAdmin && (
                <div className="ffa-admin-controls">
                  <div className="admin-stats">
                    <span>{t('pending', 'Pending')}: {pendingCount}</span>
                    <span>{t('approved', 'Approved')}: {approvedCount}/{maxPlayers}</span>
                  </div>

                  <div className="admin-buttons">
                    <button onClick={() => setShowApprovalModal(true)} className="admin-btn approval-btn">
                      <UserCheck className="w-4 h-4" />
                      {t('manageApprovals', 'Manage Approvals')} ({pendingCount})
                    </button>

                    {approvedCount >= 2 && (
                      <button onClick={() => handleCreateLobbies('random')} className="admin-btn create-lobbies-btn" disabled={loading}>
                        <Shuffle className="w-4 h-4" />
                        {t('createLobbiesRandom', 'Create Lobbies (Random)')}
                      </button>
                    )}

                    <button onClick={handleCancel} className="admin-btn cancel-btn" disabled={loading}>
                      <Trash2 className="w-4 h-4" />
                      {t('cancelTournament', 'Cancel')}
                    </button>

                    <button onClick={handleDelete} className="admin-btn delete-btn" disabled={loading}>
                      <X className="w-4 h-4" />
                      {t('deleteTournament', 'Delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Round 1 Phase */}
          {['round1_lobby', 'round1_in_progress'].includes(ffaMultistepData?.ffaStatus) && (
            <div className="ffa-round1-section">
              <h4 className="section-title">
                {t('round1', 'Round 1')} - {completedLobbies}/{lobbiesCount} {t('lobbiesComplete', 'Lobbies Complete')}
              </h4>

              <div className="ffa-lobbies-grid">
                {ffaMultistepData?.lobbies?.map((lobby, index) => (
                  <div key={index} className={`ffa-lobby-card ${lobby.status}`}>
                    <div className="lobby-header">
                      <span className="lobby-number">{t('lobby', 'Lobby')} {lobby.lobbyNumber}</span>
                      <span className={`lobby-status ${lobby.status}`}>
                        {lobby.status === 'pending' && t('pending', 'Pending')}
                        {lobby.status === 'active' && t('active', 'Active')}
                        {lobby.status === 'completed' && t('completed', 'Completed')}
                      </span>
                    </div>

                    <div className="lobby-players">
                      {lobby.players?.map((player) => {
                        const playerId = getPlayerId(player);
                        const isWinner = lobby.winner && (lobby.winner.userId?.toString() === playerId || lobby.winner.userId?._id?.toString() === playerId);
                        return (
                        <div key={playerId || player.username} className={`lobby-player ${isWinner ? 'winner' : ''}`}>
                          <div className="player-info">
                            {isWinner ? <Crown style={{ width: 12, height: 12, color: '#fbbf24' }} /> : null}
                            <span>{player.inGameName || player.username}</span>
                          </div>
                          {isAdmin && lobby.status === 'pending' && ffaMultistepData?.lobbies?.length > 1 && (
                            <div className="move-arrows">
                              <button
                                className="move-btn"
                                onClick={() => handleMovePlayer(
                                  getPlayerId(player),
                                  lobby.lobbyNumber,
                                  lobby.lobbyNumber - 1
                                )}
                                disabled={loading || lobby.lobbyNumber <= 1}
                                title="Move to previous lobby"
                              >
                                ◀
                              </button>
                              <button
                                className="move-btn"
                                onClick={() => handleMovePlayer(
                                  getPlayerId(player),
                                  lobby.lobbyNumber,
                                  lobby.lobbyNumber + 1
                                )}
                                disabled={loading || lobby.lobbyNumber >= ffaMultistepData?.lobbies?.length}
                                title="Move to next lobby"
                              >
                                ▶
                              </button>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>

                    {/* Room Info (visible to all after published) */}
                    {lobby.roomPublished && lobby.roomId && (
                      <div className="lobby-room-info">
                        <span>{t('roomId', 'Room')}: {lobby.roomId}</span>
                      </div>
                    )}

                    {/* Admin Controls for Lobby */}
                    {isAdmin && (
                      <div className="lobby-admin-controls">
                        {lobby.status === 'pending' && (
                          <button
                            onClick={() => {
                              setSelectedLobby(lobby);
                              setIsFinals(false);
                              setShowStartMatchModal(true);
                            }}
                            className="lobby-btn start-btn"
                            disabled={loading}
                          >
                            <Play className="w-3 h-3" />
                            {t('startMatch', 'Start')}
                          </button>
                        )}
                        {lobby.status === 'active' && (
                          <button
                            onClick={() => {
                              setSelectedLobby(lobby);
                              setIsFinals(false);
                              setShowSelectWinnerModal(true);
                            }}
                            className="lobby-btn winner-btn"
                            disabled={loading}
                          >
                            <Crown className="w-3 h-3" />
                            {t('selectWinner', 'Select Winner')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Finals Phase */}
          {['finals_lobby', 'finals_in_progress'].includes(ffaMultistepData?.ffaStatus) && (
            <div className="ffa-finals-section">
              <h4 className="section-title finals-title">
                <Trophy className="w-5 h-5 text-yellow-400" />
                {t('finals', 'FINALS')}
              </h4>

              <div className="ffa-finals-lobby">
                <div className="finals-status">
                  {ffaMultistepData?.finalsLobby?.status === 'pending' && t('waitingToStart', 'Waiting to Start')}
                  {ffaMultistepData?.finalsLobby?.status === 'active' && t('matchInProgress', 'Match In Progress')}
                  {ffaMultistepData?.finalsLobby?.status === 'completed' && t('finalsComplete', 'Finals Complete')}
                </div>

                <div className="finals-players">
                  {ffaMultistepData?.finalsLobby?.players?.map((player, index) => (
                    <div key={index} className={`finals-player ${ffaMultistepData?.finalsLobby?.winner?.userId?.toString() === (player.userId?.toString() || player.userId?._id?.toString()) ? 'winner' : ''}`}>
                      {ffaMultistepData?.finalsLobby?.winner?.userId?.toString() === (player.userId?.toString() || player.userId?._id?.toString()) && (
                        <Crown className="w-4 h-4 text-yellow-400" />
                      )}
                      <span className="player-name">{player.inGameName || player.username}</span>
                      <span className="from-lobby">{t('fromLobby', 'from Lobby')} {player.fromLobby}</span>
                    </div>
                  ))}
                </div>

                {/* Finals Room Info */}
                {ffaMultistepData?.finalsLobby?.roomPublished && ffaMultistepData?.finalsLobby?.roomId && (
                  <div className="finals-room-info">
                    <span>{t('roomId', 'Room')}: {ffaMultistepData?.finalsLobby?.roomId}</span>
                  </div>
                )}

                {/* Admin Controls for Finals */}
                {isAdmin && (
                  <div className="finals-admin-controls">
                    {ffaMultistepData?.finalsLobby?.status === 'pending' && (
                      <button
                        onClick={() => {
                          setSelectedLobby(ffaMultistepData?.finalsLobby);
                          setIsFinals(true);
                          setShowStartMatchModal(true);
                        }}
                        className="finals-btn start-btn"
                        disabled={loading}
                      >
                        <Play className="w-4 h-4" />
                        {t('startFinals', 'Start Finals')}
                      </button>
                    )}
                    {ffaMultistepData?.finalsLobby?.status === 'active' && (
                      <button
                        onClick={() => {
                          setSelectedLobby(ffaMultistepData?.finalsLobby);
                          setIsFinals(true);
                          setShowSelectWinnerModal(true);
                        }}
                        className="finals-btn winner-btn"
                        disabled={loading}
                      >
                        <Crown className="w-4 h-4" />
                        {t('selectGrandWinner', 'Select Grand Winner')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Completed Phase */}
          {ffaMultistepData?.ffaStatus === 'completed' && ffaMultistepData?.grandWinner && (
            <div className="ffa-completed-section">
              <div className="grand-winner-display">
                <Trophy className="w-8 h-8 text-yellow-400" />
                <div className="winner-info">
                  <span className="winner-label">{t('grandWinner', 'Grand Winner')}</span>
                  <span className="winner-name">{ffaMultistepData.grandWinner.inGameName || ffaMultistepData.grandWinner.username}</span>
                </div>
                <div className="prize-info">
                  <span>{ffaMultistepData.prizePool} {t('ptsAwarded', 'pts awarded')}</span>
                </div>
              </div>
            </div>
          )}

          {/* History Button */}
          <button className="ffa-history-btn" onClick={() => setShowHistoryModal(true)}>
            <History className="w-4 h-4" />
            {t('viewHistory', 'View History')}
          </button>
        </div>
      )}

      {/* Join Modal */}
      {showJoinModal && (
        <div className="ffa-modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="ffa-modal" onClick={e => e.stopPropagation()}>
            <div className="ffa-modal-header">
              <h3>{t('requestToJoin', 'Request to Join')}</h3>
              <button onClick={() => setShowJoinModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="ffa-modal-content">
              {tournament.password && (
                <div className="form-group">
                  <label><Lock className="w-4 h-4" /> {t('password', 'Password')}</label>
                  <input
                    type="password"
                    value={joinPassword}
                    onChange={e => setJoinPassword(e.target.value)}
                    placeholder={t('enterPassword', 'Enter password')}
                  />
                </div>
              )}
              <p className="join-info">{t('joinInfo', 'Your request will be reviewed by an admin.')}</p>
              <button className="ffa-submit-btn" onClick={handleRequestJoin} disabled={loading}>
                {loading ? t('submitting', 'Submitting...') : t('submitRequest', 'Submit Request')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && isAdmin && (
        <div className="ffa-modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="ffa-modal approval-modal" onClick={e => e.stopPropagation()}>
            <div className="ffa-modal-header">
              <h3>{t('manageApprovals', 'Manage Approvals')}</h3>
              <button onClick={() => setShowApprovalModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="ffa-modal-content">
              {/* Pending Players */}
              <div className="approval-section">
                <div className="section-header">
                  <h4>{t('pendingPlayers', 'Pending Players')} ({pendingCount})</h4>
                  {pendingCount > 0 && (
                    <button className="approve-all-btn" onClick={handleApproveAll} disabled={loading}>
                      <Check style={{ width: 16, height: 16 }} />
                      {t('approveAll', 'Approve All')}
                    </button>
                  )}
                </div>
                {pendingCount === 0 ? (
                  <p className="no-players">{t('noPendingPlayers', 'No pending players')}</p>
                ) : (
                  <div className="players-list">
                    {ffaMultistepData?.pendingPlayers?.map((player, index) => (
                      <div key={index} className="player-item">
                        <span className="player-name">{player.inGameName || player.username}</span>
                        <div className="player-actions">
                          <button
                            className="approve-btn"
                            onClick={() => handleApprovePlayer(getPlayerId(player))}
                            disabled={loading}
                            title="Approve"
                          >
                            ✓
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleRejectPlayer(getPlayerId(player))}
                            disabled={loading}
                            title="Reject"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Approved Players */}
              <div className="approval-section">
                <h4>{t('approvedPlayers', 'Approved Players')} ({approvedCount})</h4>
                {approvedCount === 0 ? (
                  <p className="no-players">{t('noApprovedPlayers', 'No approved players')}</p>
                ) : (
                  <div className="players-list">
                    {ffaMultistepData?.approvedPlayers?.map((player, index) => (
                      <div key={index} className="player-item approved">
                        <span className="player-name">{player.inGameName || player.username}</span>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemovePlayer(getPlayerId(player))}
                          disabled={loading}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Start Match Modal */}
      {showStartMatchModal && isAdmin && (
        <div className="ffa-modal-overlay" onClick={() => setShowStartMatchModal(false)}>
          <div className="ffa-modal" onClick={e => e.stopPropagation()}>
            <div className="ffa-modal-header">
              <h3>{isFinals ? t('startFinals', 'Start Finals') : t('startMatch', 'Start Match') + ' - Lobby ' + selectedLobby?.lobbyNumber}</h3>
              <button onClick={() => setShowStartMatchModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="ffa-modal-content">
              <div className="form-group">
                <label><MapPin className="w-4 h-4" /> {t('roomId', 'Room ID')} *</label>
                <input
                  type="text"
                  value={roomId}
                  onChange={e => setRoomId(e.target.value)}
                  placeholder={t('enterRoomId', 'Enter room ID')}
                />
              </div>
              <div className="form-group">
                <label><Link2 className="w-4 h-4" /> {t('roomUrl', 'Room URL')} ({t('optional', 'Optional')})</label>
                <input
                  type="text"
                  value={roomUrl}
                  onChange={e => setRoomUrl(e.target.value)}
                  placeholder={t('enterRoomUrl', 'Enter room URL')}
                />
              </div>
              <p className="modal-info">{t('roomInfoNote', 'Room info will be sent to players in this match only.')}</p>
              <button className="ffa-submit-btn" onClick={handleStartMatch} disabled={loading || !roomId.trim()}>
                {loading ? t('starting', 'Starting...') : t('startMatch', 'Start Match')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Winner Modal */}
      {showSelectWinnerModal && isAdmin && (
        <div className="ffa-modal-overlay" onClick={() => setShowSelectWinnerModal(false)}>
          <div className="ffa-modal" onClick={e => e.stopPropagation()}>
            <div className="ffa-modal-header">
              <h3>{isFinals ? t('selectGrandWinner', 'Select Grand Winner') : t('selectWinner', 'Select Winner') + ' - Lobby ' + selectedLobby?.lobbyNumber}</h3>
              <button onClick={() => setShowSelectWinnerModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="ffa-modal-content">
              <div className="winner-selection-list">
                {(isFinals ? ffaMultistepData?.finalsLobby?.players : selectedLobby?.players)?.map((player, index) => (
                  <button
                    key={index}
                    className="winner-option"
                    onClick={() => handleSelectWinner(getPlayerId(player))}
                    disabled={loading}
                  >
                    <Crown style={{ width: 16, height: 16 }} />
                    <span>{player.inGameName || player.username}</span>
                    <ArrowRight style={{ width: 16, height: 16 }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="ffa-modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="ffa-modal history-modal" onClick={e => e.stopPropagation()}>
            <div className="ffa-modal-header">
              <h3>{t('matchHistory', 'Match History')}</h3>
              <button onClick={() => setShowHistoryModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="ffa-modal-content">
              <div className="history-list">
                {ffaMultistepData?.matchHistory?.slice().reverse().map((entry, index) => (
                  <div key={index} className="history-entry">
                    <span className="history-time">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                    <span className="history-action">{entry.action.replace(/_/g, ' ')}</span>
                    {entry.details && (
                      <span className="history-details">
                        {JSON.stringify(entry.details).substring(0, 50)}...
                      </span>
                    )}
                  </div>
                ))}
                {(!ffaMultistepData?.matchHistory || ffaMultistepData?.matchHistory.length === 0) && (
                  <p className="no-history">{t('noHistory', 'No history yet')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FFAMultistepCard;
